import Image from 'next/image';
import styled from 'styled-components';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';

import { Hub } from '@shared/enums/hub';
import { Title } from '@components/atoms';
import { HubButton, hubButtons } from '@shared/constants/hubs';

type HubSelectorMobileProps = {
  hub: Hub;
  setHub: Dispatch<SetStateAction<Hub>>;
};

function HubSelectorMobile({ hub, setHub }: HubSelectorMobileProps) {
  const [open, setOpen] = useState(false);
  const [selectedHub, setSelectedHub] = useState<HubButton>();

  const selected = selectedHub?.id === hub;
  const icon = selected ? selectedHub.selectedIconM : selectedHub?.defaultIcon;

  useEffect(() => {
    const hubActive = hubButtons.find((hubItem) => hubItem.id === hub);

    setSelectedHub(hubActive!);
  }, [hub]);

  const handleOpen = () => setOpen(!open);

  const handleSelectHub = (hub: Hub) => {
    setHub(hub);
    setOpen(false);
  };

  if (!selectedHub) return null;

  return (
    <Wrapper>
      <Select onClick={handleOpen}>
        <StyledImage
          width={16}
          height={16}
          loading="lazy"
          alt="dropdown-arrow.svg"
          src="/icons/dropdown-arrow.svg"
        />

        <SelectedHub>
          <Title title={selectedHub.title} Icon={icon} fontWeight={600} />
        </SelectedHub>
      </Select>

      <DropDownList $isOpen={open}>
        {hubButtons.map(({ id, title, selectedIconM, defaultIcon }) => {
          const selected = id === hub;
          const icon = selected ? selectedIconM : defaultIcon;
          const fontWeight = selected ? 600 : 400;

          return (
            <HubItem onClick={() => handleSelectHub(id)} key={id}>
              <Title title={title} Icon={icon} fontWeight={fontWeight} />
            </HubItem>
          );
        })}
      </DropDownList>
    </Wrapper>
  );
}

const HubItem = styled.div`
  transition: 0.1s ease;

  display: flex;
  align-items: center;
  border-radius: 10px;

  height: 30px;
  padding-left: 15px;
  cursor: pointer;

  &:hover {
    background-color: ${({ theme }) => theme.colors.athensGray};
  }
`;

const SelectedHub = styled.div`
  height: 100%;

  display: flex;
  justify-content: center;

  padding-left: 20px;
`;

const DropDownList = styled.div<{ $isOpen: boolean }>`
  position: absolute;
  transition: 0.2s ease;

  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 20px 15px;

  width: 100%;
  max-width: 342px;
  height: ${({ $isOpen }) => ($isOpen ? '300px' : '50px')};
  bottom: ${({ $isOpen }) => ($isOpen ? '-305px' : '1px')};

  overflow: hidden;

  border-radius: 24px;
  background-color: ${({ theme }) => theme.colors.white};
  border: 1px solid ${({ theme }) => theme.colors.divider};

  z-index: 1;
`;

const StyledImage = styled(Image)`
  position: absolute;
  right: 10px;
  top: 20px;
`;

const Wrapper = styled.div`
  position: relative;
  margin: 20px 20px 10px;

  display: flex;
  align-items: center;
  justify-content: center;
`;

const Select = styled.div`
  transition: 0.3s ease;
  position: relative;

  display: flex;
  justify-content: flex-start;
  align-items: center;

  width: 100%;
  height: 56px;
  max-width: 342px;

  cursor: pointer;

  border-radius: 24px;
  background-color: ${({ theme }) => theme.colors.white};
  border: 1px solid ${({ theme }) => theme.colors.divider};

  z-index: 2;
`;

export default HubSelectorMobile;
